import { useCallback } from 'react';

import { PaymentMethodTypes } from '@/feature/deposit/const';

import { usePaymentMethodEnabled } from './use-payment-method-enabled';

/**
 * Custom hook that provides a helper to check if a payment method type is enabled.
 * Based on current payment method enabled flags and jurisdiction settings.
 *
 * @returns {Function} isPaymentMethodTypeEnabled - Helper function to check if a payment method type is enabled
 */
export const usePaymentMethodTypeEnabled = () => {
    const { enableTrustly, enableDebitCard, isOnlyTrustlyAndSavedDebitOnAndroid, enablePayPal, enableVenmo } =
        usePaymentMethodEnabled();

    /**
     * Helper to check if a payment method type is enabled based on flags and jurisdiction settings.
     * Accepts either a `PaymentMethodTypes` value or a raw string for convenience.
     */
    const isPaymentMethodTypeEnabled = useCallback(
        (type: PaymentMethodTypes | string) => {
            // If only Trustly and saved debit are allowed on Android, restrict to those methods
            if (isOnlyTrustlyAndSavedDebitOnAndroid) {
                switch (type) {
                    case PaymentMethodTypes.Trustly:
                        return enableTrustly;
                    case PaymentMethodTypes.Paysafe:
                    case PaymentMethodTypes.Paysafe3ds:
                        return enableDebitCard;
                    default:
                        return false;
                }
            }

            // Default behavior for other platforms
            switch (type) {
                case PaymentMethodTypes.Trustly:
                    return enableTrustly;
                case PaymentMethodTypes.Paysafe:
                case PaymentMethodTypes.Paysafe3ds:
                    return enableDebitCard;
                case PaymentMethodTypes.PaysafePaypal:
                    return enablePayPal;
                case PaymentMethodTypes.PaysafeVenmo:
                    return enableVenmo;
                default:
                    return true;
            }
        },
        [enableTrustly, enableDebitCard, enablePayPal, enableVenmo, isOnlyTrustlyAndSavedDebitOnAndroid]
    );

    return isPaymentMethodTypeEnabled;
};
